import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useCurrentUser } from '../account/useAuth';
import { useAssignment, useClassrooms, usePinAssignment } from '../teacher/useTeacher';
import './LessonLibrary.css';
import './LessonDetail.css';

/**
 * Single-assignment detail — `/teacher/lessons/:id`.
 *
 * Shows the brief, learning objectives, the rubric (criterion +
 * level descriptions), and debrief questions. Teacher-only fields
 * (teacher_notes_md) only render for teacher / admin callers; the
 * backend strips them for students.
 *
 * The rubric panel is the centerpiece — every criterion shows its
 * description AND every level's description, in plain language.
 * That's the contract we want both sides of grading to see *before*
 * the kid submits, not just after.
 */
export function LessonDetail(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const { data: user, isPending: userPending } = useCurrentUser();
  const assignment = useAssignment(id);
  const classrooms = useClassrooms();
  const pinTo = search.get('pin-to') ?? '';

  if (userPending) {
    return <div className="bl-lesson" data-testid="lesson-loading" />;
  }
  if (!user) {
    return (
      <div className="bl-lesson">
        <p className="bl-lib__notice">Sign in to view this unit.</p>
      </div>
    );
  }
  if (assignment.isPending) {
    return <p className="bl-lesson__notice">Loading…</p>;
  }
  if (assignment.isError || !assignment.data) {
    return (
      <p className="bl-lesson__notice">
        Couldn&apos;t load that unit. <Link to="/teacher/lessons">Back to the library →</Link>
      </p>
    );
  }
  const a = assignment.data;
  const classroomName = classrooms.data?.find((c) => c.id === pinTo)?.name;
  const isTeacher = user.role === 'teacher' || user.role === 'admin';

  return (
    <div className="bl-lesson">
      <header className="bl-lesson__header">
        <Link
          to={pinTo ? `/teacher/lessons?pin-to=${pinTo}` : '/teacher/lessons'}
          className="bl-lib__back"
        >
          ← Lesson library
        </Link>
        <div className="bl-lesson__title-row">
          <span className={`bl-lib__kind-badge bl-lib__kind-badge--${a.project_kind}`}>
            {a.project_kind}
          </span>
          {a.time_estimate_minutes ? (
            <span className="bl-lib__time">~{a.time_estimate_minutes} min</span>
          ) : null}
          {a.is_curated ? <span className="bl-lesson__curated-tag">Curated</span> : null}
        </div>
        <h1 className="bl-lesson__title">{a.name}</h1>
      </header>

      {isTeacher && pinTo ? (
        <PinPanel classroomId={pinTo} classroomName={classroomName ?? null} assignmentId={a.id} />
      ) : null}

      <section className="bl-lesson__brief">
        <h2 className="bl-lesson__h2">Brief</h2>
        <BriefMd markdown={a.brief_md} />
      </section>

      {a.learning_objectives.length > 0 ? (
        <section>
          <h2 className="bl-lesson__h2">Learning objectives</h2>
          <ul className="bl-lesson__list">
            {a.learning_objectives.map((obj, i) => (
              <li key={i} className="bl-lesson__list-item">
                {obj}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {a.rubric.length > 0 ? (
        <section>
          <h2 className="bl-lesson__h2">Rubric</h2>
          <p className="bl-lesson__rubric-intro">
            Here&apos;s what&apos;s being judged. Every level has a clear description so
            there&apos;s no guessing.
          </p>
          <div className="bl-lesson__rubric">
            {a.rubric.map((c) => (
              <div key={c.key} className="bl-lesson__criterion">
                <h3 className="bl-lesson__criterion-name">{c.criterion}</h3>
                <p className="bl-lesson__criterion-desc">{c.description}</p>
                <div className="bl-lesson__levels">
                  {c.levels.map((lvl) => (
                    <div key={lvl.name} className="bl-lesson__level">
                      <div className="bl-lesson__level-name">{lvl.name}</div>
                      <div className="bl-lesson__level-desc">{lvl.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {a.debrief_questions.length > 0 ? (
        <section>
          <h2 className="bl-lesson__h2">Debrief questions</h2>
          <ul className="bl-lesson__list">
            {a.debrief_questions.map((q, i) => (
              <li key={i} className="bl-lesson__list-item">
                {q}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {isTeacher && a.teacher_notes_md ? (
        <section className="bl-lesson__teacher-notes">
          <h2 className="bl-lesson__h2">Notes for the teacher</h2>
          <BriefMd markdown={a.teacher_notes_md} />
        </section>
      ) : null}

      {a.standards_tags.length > 0 ? (
        <section className="bl-lesson__standards">
          <h2 className="bl-lesson__h2">Standards</h2>
          <ul className="bl-lesson__tags">
            {a.standards_tags.map((tag) => (
              <li key={tag} className="bl-lesson__tag">
                {tag}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function PinPanel(props: {
  classroomId: string;
  classroomName: string | null;
  assignmentId: string;
}): JSX.Element {
  const pin = usePinAssignment(props.classroomId);
  return (
    <div className="bl-lesson__pin-panel">
      <span>
        Add to <strong>{props.classroomName ?? 'your classroom'}</strong>?
      </span>
      <button
        type="button"
        className="bl-lib__pin-btn"
        onClick={() => pin.mutate(props.assignmentId)}
        disabled={pin.isPending || pin.isSuccess}
      >
        {pin.isSuccess ? 'Pinned ✓' : pin.isPending ? 'Pinning…' : 'Pin to classroom'}
      </button>
    </div>
  );
}

/** Tiny markdown renderer — paragraphs + bold + headings + lists.
 *  We don't pull in a full parser; the catalogue copy is curated and
 *  the frontend treats unknown markdown as plain text. */
function BriefMd({ markdown }: { markdown: string }): JSX.Element {
  // Paragraph-split on blank lines. Headings and lists handled minimally.
  const blocks = markdown.split(/\n{2,}/);
  return (
    <div className="bl-lesson__md">
      {blocks.map((block, i) => {
        const trimmed = block.trim();
        if (trimmed.startsWith('## ')) {
          return (
            <h3 key={i} className="bl-lesson__md-h3">
              {trimmed.slice(3)}
            </h3>
          );
        }
        if (trimmed.startsWith('# ')) {
          return (
            <h2 key={i} className="bl-lesson__md-h2">
              {trimmed.slice(2)}
            </h2>
          );
        }
        if (/^[-*]\s/m.test(trimmed)) {
          const items = trimmed.split(/\n/).map((l) => l.replace(/^[-*]\s+/, ''));
          return (
            <ul key={i} className="bl-lesson__md-ul">
              {items.map((it, j) => (
                <li key={j}>{renderInline(it)}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="bl-lesson__md-p">
            {renderInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
}

function renderInline(text: string): React.ReactNode {
  // Tiny inline transformer: **bold** and *italics*.
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;
  while (remaining.length) {
    const bold = remaining.match(/\*\*(.+?)\*\*/);
    const ital = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/);
    const earliest = [bold, ital]
      .filter((m): m is RegExpMatchArray => Boolean(m))
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))[0];
    if (!earliest) {
      parts.push(remaining);
      break;
    }
    const idx = earliest.index ?? 0;
    if (idx > 0) parts.push(remaining.slice(0, idx));
    if (earliest === bold) {
      parts.push(<strong key={key++}>{earliest[1]}</strong>);
    } else {
      parts.push(<em key={key++}>{earliest[1]}</em>);
    }
    remaining = remaining.slice(idx + earliest[0].length);
  }
  return <>{parts}</>;
}
